import { useState, useEffect, useCallback } from 'react';
import { MapPin, Plus, Trash2, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminGeozones() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    nome: '', tipo: 'emergenza', centro_lat: '', centro_lng: '', raggio_km: '1.0', descrizione: '', attiva: true,
  });

  const token = localStorage.getItem('lifelink_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchZones = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/geozones`, { headers });
      setZones(data.zones || []);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchZones(); }, [fetchZones]);

  const useCurrentLocation = () => {
    if (!navigator.geolocation) return toast.error('Geolocalizzazione non disponibile');
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm(f => ({ ...f, centro_lat: String(pos.coords.latitude.toFixed(6)), centro_lng: String(pos.coords.longitude.toFixed(6)) })),
      () => toast.error('Impossibile ottenere la posizione')
    );
  };

  const createZone = async () => {
    if (!form.nome || !form.centro_lat || !form.centro_lng) {
      toast.error('Compila nome e coordinate');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/geozones`, {
        ...form,
        centro_lat: parseFloat(form.centro_lat),
        centro_lng: parseFloat(form.centro_lng),
        raggio_km: parseFloat(form.raggio_km),
      }, { headers });
      toast.success('Zona creata');
      setForm({ nome: '', tipo: 'emergenza', centro_lat: '', centro_lng: '', raggio_km: '1.0', descrizione: '', attiva: true });
      fetchZones();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore creazione');
    } finally { setCreating(false); }
  };

  const deleteZone = async (id) => {
    if (!window.confirm('Eliminare questa zona?')) return;
    try {
      await axios.delete(`${API}/admin/geozones/${id}`, { headers });
      toast.success('Zona eliminata');
      fetchZones();
    } catch (e) { toast.error('Errore'); }
  };

  const tipoColors = { emergenza: '#FF3B30', sicura: '#32D74B', evacuazione: '#FF9F0A' };

  return (
    <div className="space-y-4" data-testid="admin-geozones">
      <div className="flex items-center gap-3">
        <MapPin size={16} className="text-[#FF3B30]" />
        <span className="text-xs font-bold uppercase tracking-widest text-[#0D1B2A]">Zone Geografiche (Geofencing)</span>
        <span className="text-xs text-[#4A6B8A] uppercase tracking-widest">{zones.length} zone</span>
      </div>

      {/* Create form */}
      <div className="border border-[#B8D4E8] bg-white p-4" data-testid="geozone-form">
        <h3 className="text-xs font-bold uppercase tracking-widest text-[#4A6B8A] mb-3">Crea Nuova Zona</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            type="text" placeholder="Nome zona *" value={form.nome}
            onChange={(e) => setForm({ ...form, nome: e.target.value })}
            className="bg-[#F0F7FC] border border-[#B8D4E8] text-[#0D1B2A] px-3 py-2 text-sm focus:border-[#FF3B30] focus:outline-none"
            data-testid="geozone-nome-input"
          />
          <select
            value={form.tipo}
            onChange={(e) => setForm({ ...form, tipo: e.target.value })}
            className="bg-[#F0F7FC] border border-[#B8D4E8] text-[#0D1B2A] px-3 py-2 text-sm focus:border-[#FF3B30] focus:outline-none"
            data-testid="geozone-tipo-input"
          >
            <option value="emergenza">Emergenza</option>
            <option value="sicura">Zona Sicura</option>
            <option value="evacuazione">Evacuazione</option>
          </select>
          <input
            type="number" step="any" placeholder="Latitudine *" value={form.centro_lat}
            onChange={(e) => setForm({ ...form, centro_lat: e.target.value })}
            className="bg-[#F0F7FC] border border-[#B8D4E8] text-[#0D1B2A] px-3 py-2 text-sm focus:border-[#FF3B30] focus:outline-none font-mono"
            data-testid="geozone-lat-input"
          />
          <input
            type="number" step="any" placeholder="Longitudine *" value={form.centro_lng}
            onChange={(e) => setForm({ ...form, centro_lng: e.target.value })}
            className="bg-[#F0F7FC] border border-[#B8D4E8] text-[#0D1B2A] px-3 py-2 text-sm focus:border-[#FF3B30] focus:outline-none font-mono"
            data-testid="geozone-lng-input"
          />
          <input
            type="number" step="0.1" placeholder="Raggio km" value={form.raggio_km}
            onChange={(e) => setForm({ ...form, raggio_km: e.target.value })}
            className="bg-[#F0F7FC] border border-[#B8D4E8] text-[#0D1B2A] px-3 py-2 text-sm focus:border-[#FF3B30] focus:outline-none font-mono"
            data-testid="geozone-radius-input"
          />
          <button
            onClick={useCurrentLocation}
            className="px-3 py-2 border border-[#0A84FF] text-[#0A84FF] text-xs uppercase tracking-widest hover:bg-[#0A84FF]/10"
            data-testid="geozone-use-location-btn"
          >
            <MapPin size={12} className="inline mr-1" /> Usa Posizione Attuale
          </button>
          <textarea
            placeholder="Descrizione (opzionale)" value={form.descrizione}
            onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
            rows={2}
            className="md:col-span-2 bg-[#F0F7FC] border border-[#B8D4E8] text-[#0D1B2A] px-3 py-2 text-sm focus:border-[#FF3B30] focus:outline-none resize-none"
            data-testid="geozone-desc-input"
          />
        </div>
        <button
          onClick={createZone}
          disabled={creating}
          className="mt-3 flex items-center gap-2 px-4 py-2 bg-[#FF3B30] text-white text-xs font-bold uppercase tracking-widest hover:bg-[#FF1A1A] disabled:opacity-40"
          data-testid="create-geozone-btn"
        >
          {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Crea Zona
        </button>
      </div>

      {/* Zones list */}
      {loading ? (
        <div className="text-center py-8"><Loader2 size={24} className="animate-spin inline text-[#4A6B8A]" /></div>
      ) : zones.length === 0 ? (
        <div className="text-center py-12 border border-[#B8D4E8] bg-white">
          <MapPin size={48} className="mx-auto text-[#B8D4E8] mb-3" />
          <p className="text-sm text-[#4A6B8A]">Nessuna zona definita</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {zones.map((z) => (
            <div key={z.id} className="border border-[#B8D4E8] bg-white p-4" data-testid={`geozone-card-${z.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className="text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 border"
                      style={{ color: tipoColors[z.tipo], borderColor: `${tipoColors[z.tipo]}50`, backgroundColor: `${tipoColors[z.tipo]}15` }}
                    >
                      {z.tipo}
                    </span>
                    <span className="text-sm font-bold text-[#0D1B2A]">{z.nome}</span>
                  </div>
                  <div className="text-[10px] font-mono text-[#6B8AB5] mt-2">
                    {z.centro_lat?.toFixed(4)}, {z.centro_lng?.toFixed(4)} · raggio {z.raggio_km}km
                  </div>
                  {z.descrizione && <p className="text-xs text-[#4A6B8A] mt-2">{z.descrizione}</p>}
                </div>
                <button
                  onClick={() => deleteZone(z.id)}
                  className="p-1.5 text-[#6B8AB5] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"
                  data-testid={`delete-geozone-${z.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
